'use client';

import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';
import './pc.css';

// 链接配置
const busLink = '/introduce#apply-form';
const homeLink = 'https://ai.wenkoo.com.cn/ai-journal/?tid=2001558202349912066#/detail?id=2001558202349912066';

// Logo 数据
const logoRowOne = [
  { src: '/images/imgxingmo/logo/new-composition-junior@2x.png', alt: '新作文·初中版' },
  { src: '/images/imgxingmo/logo/yilin-composition-material@2x.png', alt: '意林·作文素材' },
  { src: '/images/imgxingmo/logo/excellent-composition-fashion-upper@2x.png', alt: '小学生优秀作文·时尚版·高年级' },
  { src: '/images/imgxingmo/logo/little-star-composition-100@2x.png', alt: '小星星·作文100分(高年级)' },
  { src: '/images/imgxingmo/logo/composition-king-upper-grade@2x.png', alt: '作文大王·中高年级' },
  { src: '/images/imgxingmo/logo/composition-review-senior@2x.png', alt: '作文评点报·高中版' },
  { src: '/images/imgxingmo/logo/composition-newsletter-junior@2x.png', alt: '作文通讯·初中版' },
  { src: '/images/imgxingmo/logo/composition-new-world-senior@2x.png', alt: '作文新天地(高中版)' },
  { src: '/images/imgxingmo/logo/composition-weekly-midterm@2x.png', alt: '作文周刊·中考读写版' },
];

const logoRowTwo = [
  { src: '/images/imgxingmo/logo/youth-literature-happy-reading@2x.png', alt: '少年文艺·开心阅读作文' },
  { src: '/images/imgxingmo/logo/happy-composition-grade-5-6@2x.png', alt: '快乐作文（5.6年级）' },
  { src: '/images/imgxingmo/logo/youth-literature-love-writing@2x.png', alt: '少年文艺·我爱写作文' },
  { src: '/images/imgxingmo/logo/classroom-composition-solo@2x.png', alt: '课堂内外·作文独唱团' },
  { src: '/images/imgxingmo/logo/happy-composition-grade-1-2@2x.png', alt: '快乐作文（1.2年级）' },
  { src: '/images/imgxingmo/logo/story-composition-upper@2x.png', alt: '故事作文·高年级' },
  { src: '/images/imgxingmo/logo/oriental-youth-reading-writing@2x.png', alt: '东方少年·阅读与作文' },
  { src: '/images/imgxingmo/logo/composition-success-gaokao@2x.png', alt: '作文成功之路·高考冲刺' },
  { src: '/images/imgxingmo/logo/composition-primary-lower@2x.png', alt: '作文·小学低年级' },
];

// Logo 滚动行组件
function LogoScrollRow({ logos, duplicateCount = 2 }: { logos: typeof logoRowOne; duplicateCount?: number }) {
  const allLogos = Array(duplicateCount).fill(logos).flat();
  return (
    <div className="logo-scroll-row">
      {allLogos.map((logo, index) => (
        <div className="logo-item" key={index}>
          <img src={logo.src} alt={logo.alt} />
        </div>
      ))}
    </div>
  );
}

// 定价卡片特性项组件
function FeatureItem({ text }: { text: string }) {
  return (
    <div className="feature-item">
      <img src="/images/imgxingmo/SuccessII.svg" alt="✓" />
      <span className="feature-item-text">{text}</span>
    </div>
  );
}

export default function XingmoPCPage() {
  const [countValue, setCountValue] = useState(0);
  const [counted, setCounted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  // 打开弹窗
  const openModal = () => {
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // 关闭弹窗
  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'auto';
  };

  // 点击遮罩层关闭
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  // ESC 键关闭弹窗
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen]);

  // 淡入效果
  useEffect(() => {
    const fadeElements = document.querySelectorAll('.fade-in-up');

    const checkFade = () => {
      fadeElements.forEach((element) => {
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 150;
        if (elementTop < window.innerHeight - elementVisible) {
          element.classList.add('visible');
        }
      });
    };

    checkFade();
    window.addEventListener('scroll', checkFade);
    window.addEventListener('resize', checkFade);

    return () => {
      window.removeEventListener('scroll', checkFade);
      window.removeEventListener('resize', checkFade);
    };
  }, []);

  // 计数动画
  useEffect(() => {
    const checkCount = () => {
      if (counted || !statsRef.current) return;

      const elementTop = statsRef.current.getBoundingClientRect().top;
      const elementVisible = 150;

      if (elementTop < window.innerHeight - elementVisible) {
        setCounted(true);
        startCountUp();
      }
    };

    const startCountUp = () => {
      const target = 10;
      const duration = 1000;
      const startTime = Date.now();

      const updateCount = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = Math.floor(target * easeOutQuart);

        setCountValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(updateCount);
        } else {
          setCountValue(target);
        }
      };

      requestAnimationFrame(updateCount);
    };

    checkCount();
    window.addEventListener('scroll', checkCount);

    return () => {
      window.removeEventListener('scroll', checkCount);
    };
  }, [counted]);

  return (
    <div className="xingmo-pc">
      {/* Header */}
      <header>
        <div className="container">
          <div className="header-container">
            <div className="logo">
              <Link href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                <img src="/images/imgxingmo/xingmo-logo.png" alt="行墨 Logo" />
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-container">
            <div className="hero-content">
              <h1 className="hero-title">
                省 80% 批改时间，<br />让学生的每一篇作文获得精准指导
              </h1>
              <p className="hero-description">
                教师不用熬夜逐句改，学生拿到能落地的提分方案，在这个时代之前教育者和学习者从未有过这样的机会。教师不再有心无力，学生也不再无助
              </p>
              <div className="hero-cta">
                <button type="button" onClick={openModal} className="btn-primary" style={{ height: 64 }}>
                  教师专属
                  <span className="btn-subtext">免费领 1 次精准批改体验</span>
                </button>
                <Link href="/introduce" className="btn-secondary" style={{ height: 64 }}>
                  了解机构版
                  <span className="btn-subtext">年级组一起省时间</span>
                </Link>
              </div>
              <p className="hero-legal">
                注册即表示您同意<a href="#">条款与条件</a>和<a href="#">隐私政策</a>。
              </p>
            </div>
            <div className="hero-media">
              <div className="video-placeholder">
                <video autoPlay muted loop playsInline>
                  <source src="/images/imgxingmo/video.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logo Showcase Section */}
      <section className="logo-showcase">
        <div className="container">
          <h2 className="section-title">230家权威作文刊物、超过300万篇范文</h2>
          <div className="logo-scroll-container">
            <div className="logo-scroll">
              <LogoScrollRow logos={logoRowOne} duplicateCount={2} />
            </div>
            <div className="logo-scroll">
              <LogoScrollRow logos={logoRowTwo} duplicateCount={3} />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section 1 */}
      <section className="features" id="features">
        <div className="container-narrow">
          <div className="feature-card">
            <div className="feature-header">
              <div>
                <h2 className="feature-title">
                  写作<br />从理解题目开始
                </h2>
                <p className="feature-subtitle">对标考题拆解，学生审题不跑题</p>
              </div>
            </div>
            <div className="feature-content">
              <div className="feature-box fade-in-up">
                <div className="box-title">
                  <div className="box-title-bar"></div>
                  <h3 className="box-title-text">题目</h3>
                </div>
                <div className="box-content">
                  <p>
                    近年来，淄博的&ldquo;烧烤&rdquo;，重庆的&ldquo;洪崖洞&rdquo;，榕江的&ldquo;村超&rdquo;，这&ldquo;一食&rdquo;&ldquo;一景&rdquo;&ldquo;一事&rdquo;，成为地方形象的靓丽名片。究其原因，既有新媒体平台传播中他人的表达和塑造，也有当地主动亮出特色、挖掘潜力、积极借势的自我塑造。在他塑与自塑的共同作用下，地方形象广为人知。
                  </p>
                  <p>材料中的&ldquo;他塑与自塑&rdquo;，引发了你怎样的联想和思考？请以&ldquo;他塑与自塑&rdquo;为题目，联系现实，写一篇议论文。</p>
                  <p>要求：论点明确，论据充实，论证合理；语言流畅，书写清晰。</p>
                </div>
              </div>
              <div className="feature-box fade-in-up">
                <div className="box-title">
                  <div className="box-title-bar"></div>
                  <h3 className="box-title-text">题目分析</h3>
                </div>
                <div className="box-content">
                  <p>
                    本题提出一类网红现象：淄博的&ldquo;烧烤&rdquo;，重庆的&ldquo;洪崖洞&rdquo;，榕江的&ldquo;村超&rdquo;，这&ldquo;一食&rdquo;&ldquo;一景&rdquo;&ldquo;一事&rdquo;，成为地方形象的靓丽名片。
                  </p>
                  <p>
                    针对这个现象，命题人分析了两个原因：一是新媒体平台传播中他人的表达和塑造，此为他塑；二是当地主动亮出特色、挖掘潜力、积极借势的自我塑造，此为自塑。命题人希望考生能够阐释这些网红地方形象的他塑与自塑的关系。当然论述过程中，重点谈他塑或自塑都可以，只不过要注意分析二者的关系。
                  </p>
                  <p>
                    这类题目看似写起来容易，但因为大多数同学能够理解自塑当然是根本，他塑是优化这类的观点，经常会说几句就不知道应该说什么了。
                  </p>
                  <p>下面这篇学生原文就明显看出来他写作的困难。</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section 2 */}
      <section className="features">
        <div className="container-narrow">
          <div className="feature-card">
            <div
              className="feature-content"
              style={{ gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'center' }}
            >
              <div className="feature-header">
                <div>
                  <h2 className="feature-title">
                    批改不止打分：<br />
                    帮你把<span className="gradient-text">&ldquo;哪里不好&rdquo;</span>
                    <br />
                    变成<span className="gradient-text">&ldquo;怎么改好&rdquo;</span>
                    <br />
                    省掉二次讲解的时间
                  </h2>
                  <p className="feature-subtitle">
                    客观评价学生写作的优点与不足，不能止步于单纯的优劣评判，更要向学生拆解评价背后的逻辑；给出的建议也不能流于表面，而是要帮助学生精准定位问题根源，让他们不仅清楚问题所在，更能切实把握下一次写作的提升契机。
                  </p>
                </div>
              </div>
              <div className="feature-box blue fade-in-up">
                <div className="box-title blue">
                  <div className="box-title-bar"></div>
                  <h3 className="box-title-text">作文评析</h3>
                </div>
                <div className="box-content">
                  <p>
                    本文在用淄博的烧烤、重庆的洪崖洞等网红地点引入话题，提出这类网红现象的出现有其必然性，这是新媒体宣传的结果。然后虽有新媒体他塑，但首先要自塑。这算是常规的观点，然后就开始分析他塑与自塑的关系，通过对这几个网红地点的分析，指出自塑是基础。
                  </p>
                  <p>当然道理是这个道理，下边似乎就有点无话可说了。</p>
                  <p>于是接着又说各种事也离不开他塑，也举了例子，说明他塑很重要。</p>
                  <p>说完他塑重要之后，又改说不能忘了自塑的本心。</p>
                  <p>总之，就是他塑与自塑的作用来回说，最终也看不出一个明确的写作思路。</p>
                  <p>这时，我们不妨先梳理一下题目所给的几个网红地点的特点，再分析一下这些地区的形象塑造的原理与过程。</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section 3 */}
      <section className="features">
        <div className="container-narrow">
          <div className="feature-card">
            <div className="feature-header">
              <div>
                <h2 className="feature-title">
                  教师不用写教案：<br />
                  修改范文 + 实操方法，直接给学生
                </h2>
                <p className="feature-subtitle">
                  引导学生将修改意见与写作建议落到实处，为他们展示高水平应用的具体成果与实操方法；写作指导不能只批判，不示范。
                </p>
              </div>
            </div>
            <div className="feature-content" style={{ display: 'flex', justifyContent: 'center' }}>
              <div
                style={{
                  maxWidth: 1140,
                  height: 660,
                  padding: '80px 200px 0px 200px',
                  borderRadius: 20,
                  background: 'rgba(23, 101, 255, 0.05)',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <div
                  className="feature-box purple fade-in-up"
                  style={{ maxWidth: 800, width: '100%', position: 'relative', background: 'white', margin: '0 auto' }}
                >
                  <div className="box-title purple">
                    <div className="box-title-bar"></div>
                    <h3 className="box-title-text">教师修改文</h3>
                  </div>
                  <div className="box-content">
                    <div style={{ textAlign: 'center', marginBottom: 20, paddingTop: 40 }}>
                      <h3
                        style={{
                          fontSize: 'var(--font-size-2xl)',
                          fontWeight: 700,
                          color: 'var(--color-text-primary)',
                        }}
                      >
                        他塑与自塑
                      </h3>
                    </div>
                    <p>
                      近年来，淄博的&ldquo;烧烤&rdquo;，重庆的&ldquo;洪崖洞&rdquo;，榕江的&ldquo;村超&rdquo;等现象成为了网红现象。这其中自然少不了媒体的倾力打造。就以淄博烧烤为例吧，各种社交媒体上到处都是淄博人热情揽客的事例，到处都在讲疫情期间隔离大学生回馈当地的故事，也到处都在讲淄博如何为烧烤营造良好经商环境的措施：一下子，淄博烧烤火遍全网，据说某个五一假期，各地到淄博的高铁票是一票难求。
                    </p>
                    <p>不仅淄博&ldquo;烧烤&rdquo;，重庆&ldquo;洪崖洞&rdquo;，榕江&ldquo;村超&rdquo;也有类似的传播效应。</p>
                    <p>
                      看到这些，人们不免感叹：这些真是网络时代营销的经典案例，而在这个网络时代，网红的诞生真是那么容易，又是那么不可思议。
                    </p>
                    <p>
                      于是有人提出，这是一个需要炒作的时代，再好的产品或服务如果没有媒体平台的他塑，也不可能成为某种经济现象；相反即使自身的产品或服务还不那么完善，有了流量的加持，至少可以先赚上一笔。
                    </p>
                    <p>
                      诚然，酒好也怕巷子深。如果不让人找到酒坊的位置，只让远处的人闻到酒香，那酒的价值也无从谈起，可见他塑宣传对于产品或服务的至关重要的作用。
                    </p>
                    <p>
                      但是，再好的他塑，也不及产品和服务本身的品质重要。对外的宣传确实可以让人知道酒坊所在，但到了酒坊喝了酒之后，如果口感不佳，那再怎么炒作也没有用。
                    </p>
                    <p>
                      回到淄博&ldquo;烧烤&rdquo;，你看网上各种媒体的讲述都是淄博&ldquo;烧烤&rdquo;本身就具有的品质。如果没有淄博人对于&ldquo;烧烤&rdquo;品味的孜孜以求，没有他们对于公平价格童叟无欺的制度建设，没有他们举全市之力为外来游客提供服务的热情态度，也就无从感动那么多自媒体大V为淄博免费宣传。
                    </p>
                    <p>
                      相反，那些整天价想着炒作、流量的人，为了流量无所不用其极，甚至以丑为美、以恶俗当噱头，这些人固然可以获取一时的关注，但这种关注只能证明这类人与事的丑陋，根本不可能获得什么利益。
                    </p>
                    <p>
                      中国自古就有谚语：桃李不言，下自成蹊。因为桃李本身的魅力，就会吸引人们来到桃李树下。同样，像淄博的&ldquo;烧烤&rdquo;，重庆的&ldquo;洪崖洞&rdquo;，榕江的&ldquo;村超&rdquo;这样的网红景象，也是因为其对自身品质的塑造而最终获得流量的加持。
                    </p>
                    <p>
                      外因是条件，内因是根本；他塑可以创造形象，自塑才能拥有品质：这自古以来的真理，在这个网红遍地的时代，仍然是颠扑不破的真理。
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 100,
                    background: 'linear-gradient(180deg, rgba(243, 247, 255, 0) 0%, #F3F7FF 70%, #F3F7FF 100%)',
                    pointerEvents: 'none',
                    borderRadius: '0 0 20px 20px',
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section 4 */}
      <section className="features">
        <div className="container-narrow">
          <div className="feature-card">
            <div className="feature-header">
              <div>
                <h2 className="feature-title">知其所以然</h2>
                <p className="feature-subtitle">
                  对示范文本展开针对性解读，使文本的内涵得到更充分的理解，引导学生精准把握写作思路与表达技巧，为自主创作筑牢基础。
                </p>
              </div>
            </div>
            <div
              className="feature-content"
              style={{ display: 'flex', alignItems: 'flex-end', gap: 0, position: 'relative' }}
            >
              <div className="feature-box purple fade-in-up" style={{ maxWidth: 760, zIndex: 1 }}>
                <div className="box-title purple">
                  <div className="box-title-bar"></div>
                  <h3 className="box-title-text">修改说明</h3>
                </div>
                <div className="box-content">
                  <p>
                    文章开始用一段的篇幅详述了淄博烧烤的火爆，这段叙述在作用于形象化地展现媒体他塑的效果，然后带一句另外两处景点也有这样的火爆。如此一写，媒体他塑的作用就体现出来了。
                  </p>
                  <p>
                    而这种媒体他塑是网络时代营销，于是就有人提出，&ldquo;这是一个需要炒作的时代，再好的产品或服务如果没有媒体平台的他塑，也不可能成为某种经济现象；相反即使自身的产品或服务还不那么完善，有了流量的加持，至少可以先赚上一笔。&rdquo;
                  </p>
                  <p>基于网红地区的火爆现象，有人提出现在需要炒作，因为有了流量的加持可以先赚一笔。</p>
                  <p>问题的提出，使得本文有了针对性，后边强调自塑是基础就有根基。</p>
                  <p>
                    自然，本文想表达的是自塑才拥有品质这一观点，那么既然有人提出炒作、流量等他塑概念，本文也就针对这种观点提出自己的看法。
                  </p>
                  <p>
                    首先自然是承认他塑宣传的作用，这是行文周密性的要求。更重要的是回到淄博烧烤现象，分析为什么这里会火爆起来。原来&ldquo;如果没有淄博人对于&lsquo;烧烤&rsquo;品味的孜孜以求，没有他们对于公平价格童叟无欺的制度建设，没有他们举全市之力为外来游客提供服务的热情态度，也就无从感动那么多自媒体大V为淄博免费宣传&rdquo;，归根到底，还是淄博烧烤的品质过硬。
                  </p>
                  <p>
                    同时，又从反面论述：&ldquo;那些整天价想着炒作、流量的人，为了流量无所不用其极，甚至以丑为美、以恶俗当噱头，这些人固然可以获取一时的关注，但这种关注只能证明这类人与事的丑陋，根本不可能获得什么利益。&rdquo;
                  </p>
                  <p>
                    正反两方面分析了淄博烧烤现象之后，他塑与自塑的关系此时已经清晰。于是，本文做了提炼总结：&ldquo;外因是条件，内因是根本；他塑可以创造形象，自塑才能拥有品质&rdquo;，这种思维方式才是今天这个网络时代应该有的。
                  </p>
                </div>
              </div>
              <div
                className="radar-chart-container fade-in-up"
                style={{ padding: 32, marginBottom: 40, zIndex: 2, position: 'absolute', right: 0 }}
              >
                <div className="radar-chart">
                  <img
                    src="/images/imgxingmo/radar-chart@2x.png"
                    alt="作文分析雷达图"
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section 5 */}
      <section className="features">
        <div className="container-narrow">
          <div className="feature-card">
            <div className="feature-content" style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
              <div style={{ flex: 1 }}>
                <h2 className="feature-title">更严谨的AI</h2>
                <p className="feature-subtitle">
                  所有的方法论必须符合行墨专家组的框架，参考内容不会以整个互联网，而是从专家批改库和经过三审三校的优秀作文文章，保障建议的正确性、正能量、安全性的保障
                </p>
              </div>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <img src="/images/imgxingmo/image1@2x.png" alt="AI技术插图" style={{ maxWidth: '100%', height: 'auto' }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing" id="pricing">
        <div className="container-narrow">
          <h2 className="pricing-title">选择适合的套餐计划</h2>
          <div className="pricing-cards">
            {/* Free Plan */}
            <div className="pricing-card">
              <div className="pricing-card-header">
                <h3 className="pricing-card-title">免费版</h3>
                <p className="pricing-card-description">大模型能力 + 专业知识库，模拟名师个性化辅导学生作文能力</p>
              </div>
              <div className="pricing-card-price">
                <div className="price-amount" style={{ marginBottom: 30 }}>
                  <span className="price-currency">￥</span>
                  <span className="price-number">0</span>
                </div>
              </div>
              <div className="pricing-card-cta">
                <button type="button" onClick={openModal} className="btn-pricing secondary">免费体验</button>
              </div>
              <div className="pricing-card-features">
                <FeatureItem text="每月5次批改" />
              </div>
            </div>

            {/* Personal Plan */}
            <div className="pricing-card recommended">
              <div className="recommended-badge">
                <img
                  src="/images/imgxingmo/recommend@2x.png"
                  alt="推荐"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </div>
              <div className="pricing-card-header">
                <h3 className="pricing-card-title">行墨个人版</h3>
                <p className="pricing-card-description">
                  个性化名师指导作文批改报告，基于名师教师批改库和230种作文期刊的范文库
                </p>
              </div>
              <div className="pricing-card-price">
                <div className="price-amount">
                  <span className="price-currency">￥</span>
                  <span className="price-number">198</span>
                </div>
                <p className="price-discount">原价 ￥498， 限时-￥300</p>
              </div>
              <div className="pricing-card-cta">
                <button type="button" onClick={openModal} className="btn-pricing primary">购买</button>
              </div>
              <div className="pricing-card-features">
                <FeatureItem text="每月30次批改" />
                <FeatureItem text="5个作文智能体" />
              </div>
            </div>

            {/* Institutional Plan */}
            <div className="pricing-card">
              <div className="pricing-card-header">
                <h3 className="pricing-card-title">行墨校园机构版</h3>
                <p className="pricing-card-description">
                  以AI赋能作文教学，附赠优质期刊资源，北师大二附中、清华附中等名校已率先启用
                </p>
              </div>
              <div className="pricing-card-cta">
                <Link href={busLink} className="btn-pricing dark">联系我们</Link>
              </div>
              <div className="pricing-card-features">
                <FeatureItem text="班级创建和长期提升分析" />
                <FeatureItem text="支持批量上传" />
                <FeatureItem text="支持全班报告一键打印" />
                <FeatureItem text="支持微信和钉钉和SSO登陆" />
                <FeatureItem text="获赠龙源校园作文期刊阅览室" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats" id="stats">
        <div className="container">
          <div className="stats-container" ref={statsRef}>
            <h2 className="stats-title">平均提升学生作文水平</h2>
            <div className="stats-number">
              <span className="count-up">{countValue}</span>%
            </div>
            <p className="stats-description">
              使用行墨的机构版，学生的中文写作成绩平均每年可提升
              10%。数据全程私密安全，学习效率同步提高，不必要的沟通冗余减少，学生能更专注于内容本身，而非纠结文字的表达形式。
            </p>
            <div className="stats-cta">
              <Link
                href="/introduce"
                className="btn-pricing secondary"
                style={{
                  backgroundColor: 'white',
                  color: 'var(--color-primary)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                了解行墨机构版
              </Link>
              <Link
                href={busLink}
                className="btn-pricing secondary"
                style={{
                  borderColor: 'white',
                  color: 'white',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                联系销售
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="footer" id="about">
        <div className="container">
          <div className="footer-container">
            <h2 className="footer-title">
              行墨<br />
              让你的工作学习事半功倍
            </h2>
            <p className="footer-subtitle">
              深耕十余年的AI写作领军者，凭借成熟技术与专业能力为你赋能，优化工作流程，
              <br />
              大幅提升效率，助力高效达成目标
            </p>
            <div className="footer-cta">
              <button type="button" onClick={openModal} className="btn-pricing primary" style={{ maxWidth: 390 }}>
                免费注册
              </button>
            </div>
            <p className="footer-legal">
              注册即表示您同意<a href="#">条款与条件</a>和<a href="#">隐私政策</a>。
            </p>
          </div>
        </div>
      </footer>

      {/* Teacher Modal */}
      {isModalOpen && (
        <div className="modal-overlay active" onClick={handleOverlayClick}>
          <div className="modal-content">
            <button type="button" className="modal-close" onClick={closeModal}>
              &times;
            </button>
            <h2 className="modal-title">行墨个人版</h2>
            <p className="modal-subtitle">手机微信扫码进入个人版小程序</p>
            <div className="modal-qrcode">
              <img src="/images/imgxingmo/mobildqrcode.png" alt="行墨个人版小程序二维码" />
            </div>
            <p className="modal-instruction">
              使用微信扫描上方二维码，即可进入行墨个人版小程序，体验AI作文批改服务。
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
